import { Phone, MessageCircle, ArrowRight, Heart, Cake, Crown } from "lucide-react";
import { Reveal, Eyebrow, CTAButton, PageHero } from "@/components/bits";
import { IMAGES, ASSETS, REETI_REEWAJ, BIRTHDAY_SERVICES, BRAND, waLink } from "@/data/site";

const ServiceChips = ({ items }) => (
  <div className="mt-8 flex flex-wrap gap-2">
    {items.map((s) => (
      <span key={s} className="border border-white/12 bg-white/[0.03] px-4 py-2 text-xs font-semibold text-zinc-200 transition-colors hover:border-red-500/60 hover:text-red-300">
        {s}
      </span>
    ))}
  </div>
);

const Events = () => (
  <>
    <PageHero
      eyebrow="Events & Entertainment"
      title={<>We don't just perform. <span className="text-red-500">We produce moments.</span></>}
      sub="From weddings to birthdays to celebration stages — cinematic event planning, choreography and live entertainment by New Boogie Woogie Academy."
      img={IMAGES.stage}
    />

    <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 sm:py-28" data-testid="reeti-reevaj">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal className="img-zoom relative overflow-hidden border border-white/10 bg-black">
          <img src={ASSETS.ritiRiwaz} alt="Riti Riwaz Event Planner — official brand" className="aspect-[4/5] w-full object-contain p-10 sm:p-14" loading="lazy" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3">
            <Heart size={18} className="text-red-500" aria-hidden="true" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.26em] text-zinc-300">Weddings · Sangeet · Mehendi · Haldi</span>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Eyebrow>Riti Riwaz Event Planner</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Turning special moments into <span className="text-red-500">unforgettable performances</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
              From the bride &amp; groom's grand entry to full sangeet choreography — our team plans, choreographs
              and performs every moment of your celebration with cinematic precision.
            </p>
            <ServiceChips items={REETI_REEWAJ} />
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <CTAButton href={waLink("Hello New Boogie Woogie Academy, I would like to plan my event with Riti Riwaz Event Planner.")} testid="reeti-plan-btn">
                Plan Your Event <ArrowRight size={15} />
              </CTAButton>
              <CTAButton href="/booking?mode=enquiry" variant="ghost" testid="reeti-enquire-btn">Enquire Now</CTAButton>
            </div>
          </Reveal>
        </div>
      </div>
      <Reveal className="mt-14">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-red-500">Watch us in action</p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="reeti-videos">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="overflow-hidden border border-white/10 bg-black" data-testid={`reeti-video-${i}`}>
              <video
                src={`/assets/rr-video-${i}.mp4`}
                poster={`/assets/rr-poster-${i}.jpg`}
                controls
                playsInline
                preload="none"
                className="aspect-video w-full object-cover"
              />
            </div>
          ))}
        </div>
      </Reveal>
    </section>

    <section className="border-y border-white/10 bg-[#08080B] py-20 sm:py-28" data-testid="birthday-section">
      <div className="mx-auto grid max-w-[1440px] items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <div className="max-lg:order-2">
          <Reveal>
            <Eyebrow>Birthday Party Entertainment</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Birthdays that feel like a <span className="text-red-500">headline show</span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
              Live rock band, singing, anchoring, dance performances, decoration and a show-stopping birthday
              boy/girl entry — a complete entertainment production for your special day.
            </p>
            <ServiceChips items={BIRTHDAY_SERVICES} />
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <CTAButton href={waLink("Hello New Boogie Woogie Academy, I would like to plan a birthday party entertainment experience.")} testid="birthday-plan-btn">
                <Cake size={15} /> Plan a Birthday Experience
              </CTAButton>
              <CTAButton href={BRAND.phoneLink} variant="ghost" testid="birthday-call-btn">
                <Phone size={15} /> Call Now
              </CTAButton>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="img-zoom relative overflow-hidden border border-white/10 max-lg:order-1">
          <img src={IMAGES.birthday} alt="Birthday party celebration and entertainment" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3">
            <Cake size={18} className="text-red-500" aria-hidden="true" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.26em] text-zinc-200">Rock Band · Anchoring · Grand Entry</span>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 sm:py-28" data-testid="miss-beauty">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Reveal>
            <Eyebrow>An Exclusive Platform for Mothers</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-black uppercase leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Mrs. <span className="text-red-500">Beauty Mom</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
              A special platform exclusively for mothers — celebrating confidence, grace and individuality on the
              ramp and beyond.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Ramp Show", "Beauty Contest"].map((s) => (
                <span key={s} className="flex items-center gap-2 border border-red-600/40 bg-red-600/10 px-5 py-2.5 font-display text-[11px] font-extrabold uppercase tracking-[0.16em] text-red-300">
                  <Crown size={13} aria-hidden="true" /> {s}
                </span>
              ))}
            </div>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <CTAButton href={waLink("Hello New Boogie Woogie Academy, I would like to know more about Mrs. Beauty Mom.")} testid="miss-beauty-enquire-btn">
                Enquire Now <ArrowRight size={15} />
              </CTAButton>
              <CTAButton href={BRAND.phoneLink} variant="ghost" testid="miss-beauty-call-btn">
                <Phone size={15} /> Call Now
              </CTAButton>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="img-zoom relative overflow-hidden border border-white/10 bg-black">
          <img src={ASSETS.mrsBeautyMom} alt="Mrs. Beauty Mom — official brand" className="aspect-[4/5] w-full object-contain p-10 sm:p-14" loading="lazy" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3">
            <Crown size={18} className="text-red-500" aria-hidden="true" />
            <span className="font-mono2 text-[10px] uppercase tracking-[0.26em] text-zinc-300">Ramp Show · Beauty Contest</span>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-t border-white/10 bg-[#08080B] py-20 text-center" data-testid="events-cta">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <Reveal>
          <h2 className="font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">
            Let's plan your <span className="text-red-500">next celebration</span>
          </h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <CTAButton href={waLink()} variant="whatsapp" testid="events-whatsapp-btn">
              <MessageCircle size={15} /> WhatsApp Us
            </CTAButton>
            <CTAButton href="/booking?mode=enquiry" variant="ghost" testid="events-enquire-btn">Enquire Now</CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Events;
